import React, { useCallback, useEffect, useMemo, useState } from 'react'
import injectSheet from 'react-jss'
import styles from './email-input.styles'
import Bone from 'pobble-components/dist/bone'
import TagsInput from 'pobble-components/dist/tags-input'
import { useMe } from 'pobble-components/dist/pobble-user'
import { apiClient } from 'pobble-components/dist/api'
import { errorHandler } from 'pobble-components/dist/error-handler'
import * as Yup from 'yup'
import THEME from 'pobble-components/dist/theme'
import uniq from 'lodash/uniq'
import { toast } from 'pobble-components/dist/toast'

const emailSchema = Yup.object().shape({
  email: Yup.string()
    .email()
    .required()
})

const cacheEmails = (cacheKey, emails) => {
  const previouslyCachedEmailsStorage = localStorage.getItem(cacheKey)
  const previouslyCachedEmails = previouslyCachedEmailsStorage
    ? JSON.parse(previouslyCachedEmailsStorage)
    : []

  /**
   * Remove duplicates and limit storage to 50 items
   */
  const mergedEmailsToCache = uniq([
    ...emails,
    ...previouslyCachedEmails
  ]).slice(0, 50)

  localStorage.setItem(cacheKey, JSON.stringify(mergedEmailsToCache))
}

const EmailInput = ({
  classes,
  selected,
  onChange,
  disableEmailClassification,
  className,
  cacheKey
}) => {
  const { me } = useMe()

  disableEmailClassification =
    disableEmailClassification || !me.account.policy.can_see_actions

  const schoolid = me.account.teacher_type && me.account.teacher_type.school.id

  const [teachers, setTeachers] = useState(null)

  const teachersMap = useMemo(() => {
    if (!teachers) return {}
    const map = {}
    teachers.forEach(teacher => {
      map[teacher.account.email_identity.email] = teacher
    })
    return map
  }, [teachers])

  const selectedMap = useMemo(() => {
    const result = {}
    selected.forEach(item => (result[item.value] = item))
    return result
  }, [selected])

  const options = useMemo(() => {
    /**
     * Add emails of other teachers in the same school
     */
    let options = !teachers
      ? []
      : teachers.map(teacher => ({
          label: `${
            teacher.account.display_name
              ? teacher.account.display_name + ' - '
              : ''
          }${teacher.account.email_identity.email}`,
          value: teacher.account.email_identity.email
        }))

    /**
     * Add emails from cache
     */
    if (cacheKey) {
      const storage = localStorage.getItem(cacheKey)
      const items = !storage
        ? []
        : JSON.parse(storage).map(email => ({
            label: email,
            value: email
          }))
      options = [...items, ...options]
    }

    return options
  }, [teachers, cacheKey])

  const processValues = useCallback(
    values => {
      values = values.filter(item => {
        try {
          emailSchema.validateSync({ email: item.value })
        } catch (e) {
          return false
        }
        return true
      })

      const newValues = values.filter(item => !selectedMap[item.value])

      /**
       * If the change happened because user removed one of the emails
       * just setting resulting array to the state
       * No need to process it
       */
      if (!newValues.length) {
        return onChange([...values])
      }

      /**
       * Append pending non determined item to prevent ui jumping
       */
      onChange([
        ...selected,
        ...newValues.map(item => ({
          ...item,
          type: 'pending',
          bgColor: '#e8e8e8',
          borderColor: '#e8e8e8'
        }))
      ])

      const promises = []

      for (let i = 0; i < newValues.length; i++) {
        const currentItem = newValues[i]

        /**
         * If user added email and this email is a teacher
         * then just adding correct type and setting
         * new set of values to the state
         */
        const { COLORS } = THEME
        if (teachersMap[currentItem.value]) {
          currentItem.type = 'teacher'
          currentItem.tooltip = 'Teacher from your school'
          continue
        }

        /**
         * Checking if the value is correct email string
         * If not firing toast with an error
         */
        promises.push(
          emailSchema.validate({ email: currentItem.value }).then(() => {
            /**
             * Append pending non determined item to prevent ui jumping
             */
            currentItem.type = 'pending'
            currentItem.bgColor = '#e8e8e8'
            currentItem.borderColor = '#b1b1b1'

            if (disableEmailClassification) {
              /**
               * If no email classification required
               *  regard all emails as not confirmed
               */
              currentItem.type = 'notConfirmedEmail'
              currentItem.bgColor = COLORS.PURPLE.EXTRA_FADED
              currentItem.borderColor = COLORS.PURPLE.FADED
              currentItem.color = '#fff'
              currentItem.iconColor = '#fff'
            } else {
              /**
               * Check if the email confirmend and upon response
               * set the option into correct color
               */
              return apiClient
                .get('/v3/email_identities/check_if_confirmed_email', {
                  params: {
                    email: currentItem.value,
                    truemail: newValues.length < 5 // Check if email exists (but only if user pasts less then 5 emails, otherwise it will take ages to for request to succeed)
                  }
                })
                .then(res => {
                  if (!res.data.confirmed_email) {
                    currentItem.type = 'notConfirmedEmail'
                    currentItem.bgColor = 'var(--purple-)'
                    currentItem.borderColor = 'var(--purple)'
                    currentItem.color = '#fff'
                    currentItem.iconColor = '#fff'
                    currentItem.tooltip = 'Not a Pobble user yet'
                  } else {
                    currentItem.type = 'confirmedEmail'
                    currentItem.bgColor = COLORS.YELLOW.EXTRA_FADED
                    currentItem.borderColor = COLORS.YELLOW.FADED
                    currentItem.tooltip = 'Not in your school'
                  }
                })
                .catch(e => {
                  const error = e.response?.data?.errors?.[0].err
                  if (error === 'e0054') {
                    toast.error(
                      <>
                        Email <strong>{currentItem.value}</strong> doesn't exist
                      </>
                    )
                    currentItem.type = 'doesntExist'
                  } else {
                    throw e
                  }
                })
                .catch(errorHandler)
            }
          })
        )
      }

      if (!promises.length)
        return onChange([
          ...selected,
          ...newValues.filter(v => v.type !== 'doesntExist')
        ])

      Promise.all(promises).then(() =>
        onChange([
          ...selected,
          ...newValues.filter(v => v.type !== 'doesntExist')
        ])
      )

      if (cacheKey)
        cacheEmails(
          cacheKey,
          newValues.map(item => item.value)
        )
    },
    [selected, teachersMap, disableEmailClassification, selectedMap, onChange]
  )

  useEffect(() => {
    if (!disableEmailClassification) {
      apiClient
        .get(`v3/schools/${schoolid}`, {
          params: {
            include:
              'school.teacher_types,school.teacher_type.account.names,school.teacher_type.account.email_identity.email'
          }
        })
        .then(res =>
          setTeachers(
            res.data.school.teacher_types.filter(teacher => {
              return (
                teacher.account.email_identity &&
                teacher.account.email_identity.email &&
                teacher.account.id !== me.account.id
              )
            })
          )
        )
        .catch(errorHandler)
    }
  }, []) // eslint-disable-line

  return !teachers && !disableEmailClassification ? (
    <Bone className={classes.bone} />
  ) : (
    <TagsInput
      className={`${classes.input} ${className}`}
      creatable
      onChange={processValues}
      value={selected}
      placeholder="Start typing email"
      searchPromptText={null}
      promptTextCreator={label => `Share with "${label}"`}
      loadOptions={() => Promise.resolve({ options })}
    />
  )
}

export default injectSheet(styles)(EmailInput)
